import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  IconButton,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import GroupIcon from '@mui/icons-material/Group';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import ChildCareIcon from '@mui/icons-material/ChildCare';

function Groups() {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [hpKidsCount, setHpKidsCount] = useState(0);
  const [otherKidsCount, setOtherKidsCount] = useState(0);
  const [error, setError] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const logDebug = (message, data = '') => {
    console.log(`[${new Date().toISOString()}] ${message}`, data);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      logDebug('Fetching groups from /api/groups');
      const response = await fetch('/api/groups');
      logDebug(`Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        logDebug('Error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const data = await response.json();
      logDebug('Received groups data:', data);
      setGroups(data);
    } catch (err) {
      logDebug('Error fetching groups:', err.toString());
      console.error('Error fetching groups:', err);
      setError(`Fehler beim Laden der Gruppen: ${err.toString()}`);
    }
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) {
      setError('Bitte geben Sie einen Gruppennamen ein');
      return;
    }

    try {
      logDebug('Attempting to create new group:', { name: newGroupName, hp_kids: hpKidsCount, other_kids: otherKidsCount });
      const response = await fetch('/api/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name: newGroupName,
          hp_kids: hpKidsCount,
          other_kids: otherKidsCount
        }),
      });
      
      logDebug(`Create group response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        logDebug('Error creating group:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const result = await response.json();
      logDebug('Group created successfully:', result);
      
      setNewGroupName('');
      setHpKidsCount(0);
      setOtherKidsCount(0);
      setOpen(false);
      setSuccessMessage('Gruppe erfolgreich erstellt');
      await fetchGroups();
    } catch (err) {
      logDebug('Error adding group:', err.toString());
      console.error('Error adding group:', err);
      setError(`Fehler beim Erstellen der Gruppe: ${err.toString()}`);
    }
  };

  const handleUpdateGroup = async (groupId, updatedData) => {
    try {
      logDebug('Updating group:', { groupId, ...updatedData });
      const response = await fetch(`/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      setSuccessMessage('Gruppe erfolgreich aktualisiert');
      await fetchGroups();
      setEditingGroup(null);
    } catch (err) {
      console.error('Error updating group:', err);
      setError(`Fehler beim Aktualisieren der Gruppe: ${err.toString()}`);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Möchten Sie diese Gruppe wirklich löschen?')) {
      return;
    }

    try {
      const response = await fetch(`/api/groups/${groupId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      setSuccessMessage('Gruppe erfolgreich gelöscht');
      await fetchGroups();
    } catch (err) {
      console.error('Error deleting group:', err);
      setError(`Fehler beim Löschen der Gruppe: ${err.toString()}`);
    }
  };

  const CounterControl = ({ label, count, setCount, icon }) => (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2,
        backgroundColor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <Typography sx={{ ml: 1 }}>{label}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton 
          size="small" 
          onClick={() => setCount(Math.max(0, count - 1))}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
        >
          <RemoveIcon />
        </IconButton>
        <Typography sx={{ mx: 2, minWidth: '30px', textAlign: 'center' }}>
          {count}
        </Typography>
        <IconButton 
          size="small" 
          onClick={() => setCount(count + 1)}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
        >
          <AddIcon />
        </IconButton>
      </Box>
    </Paper>
  );

  const GroupCard = ({ group }) => {
    const [localHpKids, setLocalHpKids] = useState(group.hp_kids || 0);
    const [localOtherKids, setLocalOtherKids] = useState(group.other_kids || 0);
    const [localName, setLocalName] = useState(group.name);
    const isEditing = editingGroup === group.id;

    // Reset local state when editing is toggled
    useEffect(() => {
      if (isEditing) {
        setLocalHpKids(group.hp_kids || 0);
        setLocalOtherKids(group.other_kids || 0);
        setLocalName(group.name);
      }
    }, [isEditing, group.hp_kids, group.other_kids, group.name]);

    const handleSave = () => {
      if (!localName.trim()) {
        setError('Gruppenname darf nicht leer sein');
        return;
      }
      handleUpdateGroup(group.id, {
        name: localName,
        hp_kids: localHpKids,
        other_kids: localOtherKids
      });
    };

    return (
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 1 }}>
          {isEditing ? (
            <>
              <IconButton 
                onClick={() => handleDeleteGroup(group.id)}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon />
              </IconButton>
              <IconButton 
                onClick={() => setEditingGroup(null)}
                sx={{ color: 'primary.main' }}
              >
                <EditIcon />
              </IconButton>
            </>
          ) : (
            <IconButton 
              onClick={() => setEditingGroup(group.id)}
              sx={{ color: 'text.secondary' }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, pt: 5 }}>
          {isEditing ? (
            <TextField
              fullWidth
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              label="Gruppenname"
              variant="outlined"
              sx={{ mb: 2 }}
            />
          ) : (
            <Typography variant="h5" component="div" gutterBottom color="text.primary">
              {group.name}
            </Typography>
          )}
          
          {isEditing ? (
            <>
              <CounterControl 
                label="HP Kinder"
                count={localHpKids}
                setCount={setLocalHpKids}
                icon={<AccessibilityNewIcon />}
              />
              <CounterControl 
                label="Weitere Kinder"
                count={localOtherKids}
                setCount={setLocalOtherKids}
                icon={<ChildCareIcon />}
              />
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                fullWidth
                onClick={handleSave}
                sx={{ mt: 2 }}
              >
                Speichern
              </Button>
            </>
          ) : (
            <>
              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  backgroundColor: 'primary.main',
                  color: 'white'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessibilityNewIcon sx={{ mr: 1 }} />
                  <Typography>HP Kinder: {group.hp_kids || 0}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ChildCareIcon sx={{ mr: 1 }} />
                  <Typography>Weitere Kinder: {group.other_kids || 0}</Typography>
                </Box>
              </Paper>

              <Typography variant="body2" color="text.secondary">
                Gesamtanzahl Kinder: {(group.hp_kids || 0) + (group.other_kids || 0)}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" color="text.primary">
          <GroupIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Gruppen
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Neue Gruppe
        </Button>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {groups.map((group) => (
          <Grid item xs={12} sm={6} md={4} key={group.id}>
            <GroupCard group={group} />
          </Grid>
        ))}
      </Grid>

      <Dialog 
        open={open} 
        onClose={() => {
          setOpen(false);
          setNewGroupName('');
          setHpKidsCount(0);
          setOtherKidsCount(0);
          setError('');
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle>Neue Gruppe erstellen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Gruppenname"
            type="text"
            fullWidth
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            error={!!error}
            helperText={error}
            sx={{ mb: 3 }}
          />
          
          <CounterControl 
            label="HP Kinder"
            count={hpKidsCount}
            setCount={setHpKidsCount}
            icon={<AccessibilityNewIcon />}
          />
          
          <CounterControl 
            label="Weitere Kinder"
            count={otherKidsCount}
            setCount={setOtherKidsCount}
            icon={<ChildCareIcon />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpen(false);
            setNewGroupName('');
            setHpKidsCount(0);
            setOtherKidsCount(0);
            setError('');
          }}>
            Abbrechen
          </Button>
          <Button onClick={handleAddGroup} variant="contained">
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Groups;